/**
 * Typed context key and entity ID system.
 * Replaces the old Identifiers enum with type-safe helpers.
 */

/**
 * Template literal type for context keys.
 * @example CONTEXT_KEY<"Position"> = "<Position>"
 */
export type CONTEXT_KEY<T extends string> = `<${T}>`;

/**
 * Branded string type for entity IDs.
 * Provides type safety to prevent mixing IDs of different entity types.
 */
export type EntityId<T extends string> = string & { _brand: T };

/**
 * Creates a context key string from an entity name.
 * @example CONTEXT_KEY("Position") returns "<Position>"
 */
export function CONTEXT_KEY<T extends string>(entity: T): CONTEXT_KEY<T> {
  return `<${entity}>` as CONTEXT_KEY<T>;
}

/**
 * GraphQL / JSON `context` keys that are not {@link CONTEXT_KEY} entity keys.
 * Runtime uses these alongside entity keys (e.g. `debug` when data logging is on — see atom-client `useCombinedContext` and `LockedTransactionInterceptor`).
 * Codegen merges this list with per-app entity keys for Zod validation.
 */
export const NON_ENTITY_CONTEXT_KEYS = ["debug"] as const;

/**
 * Creates a branded entity ID from a string.
 * @example EntityId<"Position">("abc-123") returns "abc-123" typed as EntityId<"Position">
 */
export function EntityId<T extends string>(id: string): EntityId<T> {
  return id as EntityId<T>;
}

/**
 * Typed context values object.
 * Maps context keys to their entity IDs.
 */
export type TypedContextValues<T extends string> = {
  [K in CONTEXT_KEY<T>]?: EntityId<T> | undefined;
};

/**
 * Creates a single-entry context object for an entity.
 * @example SET_ID("Position", "abc-123") returns { "<Position>": "abc-123" }
 */
export function SET_ID<T extends string>(
  entity: T,
  id: string,
): { [K in CONTEXT_KEY<T>]: EntityId<T> } {
  return { [CONTEXT_KEY(entity)]: EntityId<T>(id) } as {
    [K in CONTEXT_KEY<T>]: EntityId<T>;
  };
}

/**
 * Builds a typed context object from entity-to-ID mappings.
 * @example buildContext({ Position: "abc", Order: "def" })
 */
export function buildContext<T extends string>(
  context: Partial<Record<T, string>>,
): TypedContextValues<T> {
  const result: TypedContextValues<T> = {} as TypedContextValues<T>;
  for (const [entity, id] of Object.entries(context)) {
    if (id !== undefined) {
      Object.assign(result, SET_ID(entity as T, id as string));
    }
  }
  return result;
}

/**
 * Type-safe iteration over context values.
 * Use this instead of Object.entries() to maintain type safety.
 *
 * @example
 * for (const [key, value] of contextEntries(context)) {
 *   // key is CONTEXT_KEY<string>, value is string
 *   transaction.setEntry(key, value);
 * }
 */
export function contextEntries<T extends string>(
  context: TypedContextValues<T>,
): Array<[CONTEXT_KEY<T>, string]> {
  return Object.entries(context).filter(
    (entry): entry is [CONTEXT_KEY<T>, string] =>
      entry[1] !== undefined && entry[1] !== null,
  );
}

/**
 * Type-safe key access for context values.
 * Returns undefined if key doesn't exist or value is null/undefined.
 */
export function getContextValue<T extends string>(
  context: TypedContextValues<T>,
  key: CONTEXT_KEY<T>,
): string | undefined {
  return (context as Record<string, string | undefined>)[key];
}

/**
 * Type-safe key assignment for context values.
 * Mutates the context object.
 */
export function setContextValue<T extends string>(
  context: TypedContextValues<T>,
  key: CONTEXT_KEY<T>,
  value: string,
): void {
  (context as Record<string, string>)[key] = value;
}
